import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Mail, Send, Users, Clock, CheckCircle } from 'lucide-react';
import { usePayrollCalculations } from '@/hooks/usePayrollCalculations';
import { usePayrollPeriods } from '@/hooks/usePayrollPeriods';
import { usePayrollNotifications } from '@/services/payrollNotificationService';
import { format } from 'date-fns';

interface PayrollEmailManagerProps {
  payrollPeriodId: string;
}

export function PayrollEmailManager({ payrollPeriodId }: PayrollEmailManagerProps) {
  const { calculations, isLoading } = usePayrollCalculations(payrollPeriodId);
  const { periods } = usePayrollPeriods();
  const { sendCalculatedNotifications, sendApprovedNotifications } = usePayrollNotifications();
  
  const [autoNotifications, setAutoNotifications] = useState(true);
  const [sendingNotifications, setSendingNotifications] = useState(false);

  const period = periods?.find(p => p.id === payrollPeriodId);

  const handleSendCalculatedNotifications = async () => {
    if (!calculations || !period) return;

    setSendingNotifications(true);
    try {
      await sendCalculatedNotifications(calculations, period.period_month, period.period_year);
    } finally {
      setSendingNotifications(false);
    }
  };

  const handleSendApprovedNotifications = async () => {
    if (!calculations || !period) return;

    setSendingNotifications(true);
    try {
      await sendApprovedNotifications(calculations, period.period_month, period.period_year);
    } finally {
      setSendingNotifications(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading email settings...</div>
        </CardContent>
      </Card>
    );
  }

  if (!period) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Payroll period not found</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Notifications
          </CardTitle>
          <CardDescription>
            Manage payroll email notifications for {format(new Date(period.start_date), 'MMMM yyyy')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="auto-notifications">Automatic Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Send emails automatically when payroll status changes
              </p>
            </div>
            <Switch
              id="auto-notifications"
              checked={autoNotifications}
              onCheckedChange={setAutoNotifications}
            />
          </div>

          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {calculations?.length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Total Employees</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {calculations?.filter(c => c.calculation_status === 'approved').length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Approved</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-600">
                {calculations?.filter(c => c.calculation_status === 'calculated').length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Pending</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Send Notifications</CardTitle>
          <CardDescription>Manually send notifications to employees</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Calculation Complete
              </h4>
              <p className="text-sm text-muted-foreground">
                Notify employees that their payroll has been calculated and is under review.
              </p>
              <Button
                onClick={handleSendCalculatedNotifications}
                disabled={sendingNotifications || !calculations?.length}
                className="w-full"
                variant="outline"
              >
                <Send className="h-4 w-4 mr-2" />
                {sendingNotifications ? 'Sending...' : 'Send Calculated Notifications'}
              </Button>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Payroll Approved
              </h4>
              <p className="text-sm text-muted-foreground">
                Notify employees that their payroll has been approved and payment is being processed.
              </p>
              <Button
                onClick={handleSendApprovedNotifications}
                disabled={sendingNotifications || period.status !== 'approved'}
                className="w-full"
              >
                <Send className="h-4 w-4 mr-2" />
                {sendingNotifications ? 'Sending...' : 'Send Approval Notifications'}
              </Button>
            </div>
          </div>

          {period.status !== 'approved' && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-800">
                <Clock className="h-4 w-4" />
                <span className="font-medium">Approval Required</span>
              </div>
              <p className="text-sm text-amber-700 mt-1">
                Approval notifications can only be sent after the payroll period has been approved.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Email Templates</CardTitle>
          <CardDescription>Preview and customize email templates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Email templates are automatically generated in German and include:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Employee name and personalized greeting</li>
              <li>Payroll period information</li>
              <li>Gross and net salary amounts</li>
              <li>Next steps and contact information</li>
              <li>Company branding and compliance notices</li>
            </ul>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="outline">German Language</Badge>
            <Badge variant="outline">Swiss Compliance</Badge>
            <Badge variant="outline">Responsive Design</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}